/**
 * Web Vitals RUM (Real User Monitoring) reporter.
 *
 * Reports Core Web Vitals (LCP, FCP, CLS, TTFB) to the backend
 * so the Performance dashboard can show real-user page-load metrics.
 *
 * Usage (in main.js):
 *   import { initWebVitals } from '@/webVitals'
 *   initWebVitals(apiClient)
 *
 * Backend endpoint: POST /api/admin/performance/web-vitals (public, no auth)
 * Stores data as system_metrics rows with metric_name='web_vital_*'.
 */

const VITALS_ENDPOINT = '/api/admin/performance/web-vitals'

let _api = null
let _reported = false

function sendVital(name, value, rating) {
  if (!_api) return
  _api.post(VITALS_ENDPOINT, {
    metric_name: `web_vital_${name.toLowerCase()}`,
    metric_value: Math.round(value * 100) / 100,
    rating: rating || 'unknown',
    url: window.location.pathname,
  }).catch(() => {
    // Silently ignore — don't break the app for telemetry
  })
}

function observeLCP() {
  try {
    new PerformanceObserver((list) => {
      const entries = list.getEntries()
      if (entries.length > 0) {
        const lcp = entries[entries.length - 1]
        sendVital('LCP', lcp.startTime, lcp.startTime < 2500 ? 'good' : lcp.startTime < 4000 ? 'needs-improvement' : 'poor')
      }
    }).observe({ type: 'largest-contentful-paint', buffered: true })
  } catch (_) { /* Safari < 17 */ }
}

function observeFCP() {
  try {
    new PerformanceObserver((list) => {
      const entries = list.getEntries()
      if (entries.length > 0) {
        const fcp = entries[0]
        sendVital('FCP', fcp.startTime, fcp.startTime < 1800 ? 'good' : fcp.startTime < 3000 ? 'needs-improvement' : 'poor')
      }
    }).observe({ type: 'paint', buffered: true })
  } catch (_) { /* ignore */ }
}

function observeCLS() {
  try {
    let clsValue = 0
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value
        }
      }
      sendVital('CLS', clsValue, clsValue < 0.1 ? 'good' : clsValue < 0.25 ? 'needs-improvement' : 'poor')
    }).observe({ type: 'layout-shift', buffered: true })
  } catch (_) { /* ignore */ }
}

function observeTTFB() {
  try {
    const nav = performance.getEntriesByType('navigation')[0]
    if (nav && nav.responseStart > 0) {
      const ttfb = nav.responseStart - nav.requestStart
      sendVital('TTFB', ttfb, ttfb < 800 ? 'good' : ttfb < 1800 ? 'needs-improvement' : 'poor')
    }
  } catch (_) { /* ignore */ }
}

/**
 * Start collecting Web Vitals and report to backend.
 * @param {Object} apiClient — the axios instance from the app (e.g. this.$api)
 */
export function initWebVitals(apiClient) {
  if (_reported) return
  _reported = true
  _api = apiClient

  // Report after page is fully settled (3s after load)
  if (document.readyState === 'complete') {
    setTimeout(reportAll, 3000)
  } else {
    window.addEventListener('load', () => {
      setTimeout(reportAll, 3000)
    }, { once: true })
  }
}

function reportAll() {
  observeLCP()
  observeFCP()
  observeCLS()
  observeTTFB()
}
